'use client';
import React from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { ArrowLeft, Shield } from 'lucide-react';

const sections = [
  {
    heading: 'Overview',
    content: 'Panorama Community Network ("the Platform") is a private, resident-operated network for Panorama Towers residents. This Privacy Policy explains how we collect, use, and protect information when you use the Platform. By using the Platform, you agree to the practices described here.',
  },
  {
    heading: 'Information We Collect',
    content: 'We collect information you provide directly when you request access, create a profile, or interact with the Platform.',
    list: [
      'Name, tower, and unit number (required for verification)',
      'Email address and phone number (visibility controlled by you)',
      'Profile information you choose to share: bio, profession, interests, services offered',
      'Content you post: conversations, marketplace listings, service listings, reviews',
      'Usage data: pages visited, features used, interaction patterns',
    ],
  },
  {
    heading: 'How We Use Your Information',
    content: 'We use your information to operate and improve the Platform.',
    list: [
      'Verify your residency and maintain the integrity of the community',
      'Display your profile to other verified residents (subject to your privacy settings)',
      'Send you notifications about platform activity relevant to you',
      'Improve the Platform based on usage patterns',
      'Enforce community guidelines and respond to reports',
    ],
  },
  {
    heading: 'What Is Visible to Other Residents',
    content: 'Your privacy settings control what other verified residents can see. By default:',
    list: [
      'Your name, tower, and role (Owner / Renter / Resident) are visible to verified residents',
      'Your unit number is visible to verified residents',
      'Your email and phone are hidden by default — you choose to show them',
      'Your bio, profession, and interests are visible if you add them',
      'Your posts, listings, and reviews are visible to verified residents',
    ],
  },
  {
    heading: 'What Is Never Shared Publicly',
    content: 'The following information is never visible to the public or to non-verified users:',
    list: [
      'Your contact details (email, phone) unless you explicitly make them visible',
      'Your unit number is not shown on public-facing pages',
      'Your full directory profile is not accessible to unverified visitors',
      'Private conversations and direct messages',
    ],
  },
  {
    heading: 'Building Management & HOA',
    content: 'This Platform is independent of Panorama Towers building management and the HOA. We do not share resident data, content, or activity with building management, the HOA, or any property management entity. Your participation on this Platform is private from building management.',
  },
  {
    heading: 'Data Retention',
    content: 'We retain your data for as long as your account is active. If you request account deletion, we will remove your profile and associated content within a reasonable timeframe. Some data may be retained for legal or safety purposes.',
  },
  {
    heading: 'Security',
    content: 'We take reasonable technical and organizational measures to protect your information. The Platform uses industry-standard security practices. However, no system is completely secure — please use good judgment about what you share.',
  },
  {
    heading: 'Your Rights',
    content: 'You have the right to:',
    list: [
      'Access the information we hold about you',
      'Correct inaccurate information in your profile',
      'Request deletion of your account and associated data',
      'Control the visibility of your contact information and profile details',
      'Opt out of non-essential communications',
    ],
  },
  {
    heading: 'Changes to This Policy',
    content: 'We may update this Privacy Policy from time to time. Significant changes will be communicated to verified residents through the Platform. Continued use of the Platform after changes constitutes acceptance of the updated policy.',
  },
  {
    heading: 'Contact',
    content: 'Questions about this Privacy Policy or your data can be directed to the Platform team through the contact information available to verified residents.',
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        {/* Back nav */}
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-3xl mx-auto px-6 lg:px-10 py-4">
            <Link href="/" className="inline-flex items-center gap-2 text-[13px] transition-colors duration-150 hover:opacity-80" style={{ color: '#A7A3C4' }}>
              <ArrowLeft size={14} /> Back to Home
            </Link>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
          {/* Header */}
          <div className="flex items-start gap-4 mb-10">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(45,212,160,0.10)', border: '1px solid rgba(45,212,160,0.20)' }}
            >
              <Shield size={20} style={{ color: '#2DD4A0' }} />
            </div>
            <div>
              <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide mb-2"
                style={{ background: 'rgba(45,212,160,0.10)', border: '1px solid rgba(45,212,160,0.20)', color: '#2DD4A0' }}
              >
                Legal
              </span>
              <h1 className="font-display font-bold text-[32px] sm:text-[38px] leading-tight" style={{ color: '#F2F4FF' }}>
                Privacy Policy
              </h1>
              <p className="text-[13px] mt-2" style={{ color: 'rgba(167,163,196,0.55)' }}>
                Last updated: March 2026 · Panorama Community Network
              </p>
            </div>
          </div>

          {/* Intro note */}
          <div
            className="px-5 py-4 rounded-xl mb-10"
            style={{ background: 'rgba(45,212,160,0.05)', border: '1px solid rgba(45,212,160,0.12)' }}
          >
            <p className="text-[13.5px] leading-relaxed" style={{ color: 'rgba(242,244,255,0.75)' }}>
              This Platform is resident-operated and independent of building management. Your privacy from building management is a core principle of how we operate.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {sections?.map((section, idx) => (
              <div key={idx}>
                <h2 className="font-display font-semibold text-[18px] mb-3" style={{ color: '#F2F4FF' }}>
                  {section?.heading}
                </h2>
                {section?.content && (
                  <p className="text-[14.5px] leading-relaxed mb-3" style={{ color: '#A7A3C4' }}>
                    {section?.content}
                  </p>
                )}
                {section?.list && (
                  <ul className="space-y-2 mt-3">
                    {section?.list?.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: '#2DD4A0' }} />
                        <span className="text-[14px] leading-relaxed" style={{ color: '#A7A3C4' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {idx < sections?.length - 1 && (
                  <div className="neon-divider mt-10" />
                )}
              </div>
            ))}
          </div>

          {/* Footer links */}
          <div
            className="mt-14 pt-8 flex flex-wrap gap-4"
            style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
          >
            <Link href="/terms-of-use" className="text-[13px] font-medium transition-colors duration-150 hover:opacity-80" style={{ color: '#4CC9FF' }}>
              Terms of Use →
            </Link>
            <Link href="/community-guidelines" className="text-[13px] font-medium transition-colors duration-150 hover:opacity-80" style={{ color: '#4CC9FF' }}>
              Community Guidelines →
            </Link>
            <Link href="/resources/privacy-and-trust" className="text-[13px] font-medium transition-colors duration-150 hover:opacity-80" style={{ color: '#4CC9FF' }}>
              Privacy & Trust Resource →
            </Link>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
